/**
 * Client usage — prototype VIEWS.cusage + uProduct (docs/prototype.html 4127-4159), at /usage/client/<id>.
 *
 * Usage by product: GET /usage/clients/{id} (each product's period, last month, change, 30-day forecast, plan,
 * six months, 14 days, other numbers, tip). Warnings: that client's live alerts from GET /usage/alerts.
 * Header facts: GET /clients/{id} (since; annual_revenue_lakh is present for managers only). What is open: the
 * client's ClientRow from GET /clients (open/overdue tasks, escalations, last contact) + open client requests from
 * GET /work?client_id&type=request + the primary contact. Ideas to lift adoption: each product's `tip`.
 * "Copy as a WhatsApp update" is built from the same served data (prototype briefText).
 * Engagement this month needs GET /clients/{id}/brief (step 6, contract still `proposed`): the panel shows only
 * once that route answers, and the brief's share text is used then.
 *
 * The "In 30 days" figure and the forecast in both charts are drawn only from a meaningful fit (usableForecast);
 * a noisy fit says FORECAST_UNEVEN instead (ADR §8.3).
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import type { ClientProductUsage, ClientRow, Product, RouteQuery } from '@amplispot/contracts';
import { call } from '../../api';
import { useClient, useClients } from '../../lib/data';
import { useBrief, STATE_CLS } from '../../lib/engagement';
import { firstName, monthYear, rupees } from '../../lib/format';
import { allPages } from '../../lib/work';
import { FORECAST_UNEVEN, arrow, signed, forecastTooUneven, groupAlerts, nf, periodLabel, toneOf, usableForecast, useAlerts, useClientUsage, useProducts } from '../../lib/usage';
import { Empty, ErrorState, Loading, PriorityChip, errorText, priorityLabel, useToast } from '../../ui/kit';
import { PageInfo } from '../../shell/Help';
import { ClientLogo } from '../clients/parts';
import { copyText } from '../timesheets/TsModals';
import { DaysLine, daysInfo, MonthBars } from './parts';
import { TypeInModal } from './TypeInModal';
import { UploadModal } from './UploadModal';

function Us({ k, v, s, cls }: { k: string; v: string; s?: string; cls?: string }) {
  return (
    <div className="us">
      <div className="k">{k}</div>
      <div className={`v ${cls ?? ''}`}>{v}</div>
      {s ? <div className="s">{s}</div> : null}
    </div>
  );
}

/** Prototype prodTip kinds: low (plain), falling (warn), strong (good). The text itself is the API's `tip`. */
const tipCls = (p: ClientProductUsage, fall: number) =>
  p.util == null || p.util < p.low_line ? '' : p.trend != null && p.trend <= -fall ? 'warn' : p.util >= 85 ? 'good' : '';

function ProductPanel(props: {
  p: ClientProductUsage;
  product: Product | undefined;
  asOf: string;
  clientName: string;
  onUpload: () => void;
  onTypeIn: () => void;
}) {
  const { p, product } = props;
  if (p.util == null)
    return (
      <div className="panel upr">
        <div className="bd">
          <div className="upt">
            <h3>{p.product_name}</h3>
            <span className="chip info">No data yet</span>
          </div>
          <p className="sub" style={{ margin: '8px 0 14px' }}>
            Nothing has been recorded for this product. Upload the product report, or type the numbers in for this client.
          </p>
          <button className="btn" onClick={props.onUpload}>
            Upload usage report
          </button>{' '}
          <button className="btn" onClick={props.onTypeIn}>
            Type in for {props.clientName}
          </button>
        </div>
      </div>
    );
  const fall = product?.fall_pct ?? 15;
  // trend is null when there is no baseline yet: show '-', never a made-up 0%.
  const tr = p.trend;
  const f = usableForecast(p.forecast);
  const uneven = forecastTooUneven(p.forecast);
  const extras = p.extras.filter((e) => e.value != null);
  const monthsEst = p.months.some((m) => m.is_estimated);
  const lastMonth = p.months.at(-1);
  const daysEst = daysInfo(p.days).est;
  const per = product?.period ?? 'month';
  return (
    <div className="panel upr">
      <div className="bd">
        <div className="upt">
          <h3>{p.product_name}</h3>
          <span className="sub">{p.main_metric}</span>
          <div className="grow" />
          <span className={`chip ${toneOf(p.util, p.low_line, p.watch_line)}`}>{Math.round(p.util)}% of plan</span>
        </div>
        <div className="usg">
          <Us k="This period" v={nf(p.used)} s={p.unit} />
          {/* Last month = the last entry of the six-month series, as the Overview tooltip reads it: a real report
              when one is in, else the estimate from the client's own usual (prototype: "Last month is the client's
              own usual until real monthly reports are uploaded"). Never the raw configured base alone. */}
          <Us
            k="Last month"
            v={nf(lastMonth?.value ?? p.base)}
            s={lastMonth && lastMonth.value != null && !lastMonth.is_estimated ? `reported for ${periodLabel(lastMonth.period)}` : 'your own usual'}
          />
          <Us
            k="Change"
            v={tr == null ? '-' : `${arrow(tr)} ${signed(tr)}`}
            s={tr == null ? 'no baseline yet' : 'on last month'}
            cls={tr == null ? '' : tr <= -fall ? 'bad' : tr < -5 ? 'warn' : tr >= 5 ? 'good' : ''}
          />
          <Us
            k="In 30 days"
            v={f ? nf(f.forecast_abs) : '-'}
            s={f ? `${f.util_in_30d}% of plan at this pace` : uneven ? FORECAST_UNEVEN : ''}
            cls={f && f.util_in_30d < p.low_line ? 'bad' : f && f.util_in_30d < p.watch_line ? 'warn' : ''}
          />
          <Us k="Plan" v={nf(p.plan)} s={`bought each ${per}`} />
        </div>
        <div className="upc">
          <div>
            <div className="lbl">Month by month</div>
            <MonthBars p={p} asOf={props.asOf} />
          </div>
          <div>
            <div className="lbl">Last 14 days and the next 30{daysEst ? ', partly estimated' : ''}</div>
            <DaysLine p={p} />
          </div>
        </div>
        {extras.length ? (
          <>
            <div className="lbl" style={{ marginTop: 6 }}>
              Other numbers
            </div>
            <div className="uex">
              {extras.map((e) => (
                <div key={e.field_key} className="ue">
                  <b>
                    {nf(e.value)}
                    {e.unit === '%' ? '%' : ''}
                  </b>
                  <span>{e.label}</span>
                </div>
              ))}
            </div>
          </>
        ) : null}
        {p.tip ? (
          <div className={`note ${tipCls(p, fall)}`} style={{ marginTop: 14 }}>
            <b>What to do.</b> {p.tip}
          </div>
        ) : null}
        <div className="sub" style={{ fontSize: 12, marginTop: 12 }}>
          {monthsEst ? 'Earlier months are estimated from the client’s own usual level until the monthly reports are uploaded. ' : ''}
          {daysEst ? 'Days without a daily figure are estimated (dashed line) from the client’s own usual level to today. ' : ''}
          The forecast keeps the last 14 days going, at a cautious pace.
        </div>
      </div>
    </div>
  );
}

/** Open client requests for one client (GET /work, every page). Members see their own, as everywhere. */
function useOpenRequests(clientId: string) {
  return useQuery({
    queryKey: ['work', 'list', { client_id: clientId, type: 'request' }, 'count'],
    queryFn: async () =>
      (
        await allPages((cursor) =>
          call('work.list', {
            query: {
              client_id: clientId,
              type: 'request',
              limit: 200,
              cursor,
            } as RouteQuery<'work.list'>,
          }),
        )
      ).length,
  });
}

/** Prototype briefText, from the served usage + client row (no engagement line until the step-6 brief answers). */
function shareText(name: string, row: ClientRow | undefined, prio: string, spoc: string, products: ClientProductUsage[], requests: number | undefined): string {
  return [
    `*${name}* (${prio} priority, SPOC ${firstName(spoc)})`,
    ...products.map(
      (p) =>
        `- ${p.product_name}: ${p.util == null || p.used == null ? 'no data yet' : `${nf(p.used)} ${p.unit} (${p.util}% of plan${p.trend == null ? '' : `, ${signed(p.trend)} vs usual`})`}`,
    ),
    row
      ? `Open: ${row.open_task_count} tasks (${row.overdue_count} overdue), ${row.open_escalations} escalations${requests != null ? `, ${requests} requests` : ''}`
      : '',
    (() => {
      const tips = products
        .filter((p) => p.tip)
        .slice(0, 2)
        .map((p) => p.tip);
      return tips.length ? `Next: ${tips.join(' ')}` : '';
    })(),
  ]
    .filter(Boolean)
    .join('\n');
}

export function ClientUsagePage({ clientId }: { clientId: string }) {
  const u = useClientUsage(clientId);
  const b = useBrief(clientId);
  const cd = useClient(clientId);
  const rows = useClients();
  const rq = useOpenRequests(clientId);
  const al = useAlerts();
  const pr = useProducts();
  const nav = useNavigate();
  const toast = useToast();
  const [upload, setUpload] = useState(false);
  const [typeIn, setTypeIn] = useState<string | null>(null);

  const warnings = useMemo(() => groupAlerts((al.data ?? []).filter((a) => a.client.id === clientId)).filter((g) => g.sev !== 'info'), [al.data, clientId]);
  const byKey = useMemo(() => new Map((pr.data ?? []).map((p) => [p.key, p])), [pr.data]);

  if (u.isLoading) return <Loading label="Loading usage…" />;
  if (u.error) return <ErrorState error={u.error} onRetry={() => void u.refetch()} />;
  const d = u.data!;
  const c = d.client;
  const brief = b.data;
  const detail = cd.data;
  const row = rows.data?.find((r) => r.id === clientId);
  const contact = detail?.contacts.find((x) => x.is_primary) ?? detail?.contacts[0];
  const ideas = d.products.filter((p) => p.tip);
  const share = brief?.share_text ?? shareText(c.name, row, priorityLabel(c.priority), c.spoc.name, d.products, rq.data);
  // "Nothing to warn about" only once GET /usage/alerts has loaded and none are live for this client — never
  // while it is loading or after it failed (the page would look healthy for a client whose usage is falling).
  const verdict = al.isLoading
    ? 'Checking for warnings…'
    : al.error
      ? null
      : warnings.length
        ? `${warnings.length} warning${warnings.length > 1 ? 's' : ''} on ${c.name}. ${warnings[0]!.why}`
        : `Nothing to warn about.${brief ? ` ${brief.where_we_stand}` : ''}`;

  return (
    <>
      <div className="split" style={{ marginBottom: 6 }}>
        <button className="btn sm ghost" onClick={() => nav('/usage/uover')}>
          ← All usage
        </button>
        <div className="grow" />
        <button className="btn sm" onClick={() => setUpload(true)}>
          Upload usage report
        </button>
        <button className="btn sm" onClick={() => copyText(share, () => toast('Copied. Paste it into WhatsApp.'))}>
          Copy as a WhatsApp update
        </button>
        <button className="btn sm" onClick={() => nav(`/clients/book?client=${encodeURIComponent(c.id)}`)}>
          Open client page
        </button>
      </div>
      <div className="ph" style={{ marginTop: 8 }}>
        <div className="split" style={{ gap: 16 }}>
          <ClientLogo id={c.id} name={c.name} size={56} />
          <div>
            <h1 style={{ margin: 0 }}>
              {c.name}
              <PageInfo helpKey="cusage" />
            </h1>
            <p>
              <PriorityChip p={c.priority} /> SPOC {c.spoc.name} · {c.segment}
              {detail ? ` · client since ${monthYear(detail.since)}` : ''}
              {detail && 'annual_revenue_lakh' in detail && detail.annual_revenue_lakh != null ? ` · ${rupees(detail.annual_revenue_lakh)} a year` : ''}
            </p>
          </div>
        </div>
      </div>
      {al.error ? (
        <div className="note bad" role="alert" style={{ marginBottom: 18 }}>
          <b>Where we stand.</b> Could not load the warnings, so this page cannot say whether {c.name} needs attention. {errorText(al.error)}{' '}
          <button type="button" className="btn sm" onClick={() => void al.refetch()} disabled={al.isFetching}>
            {al.isFetching ? 'Trying…' : 'Try again'}
          </button>
        </div>
      ) : (
        <div className={`note ${warnings.some((w) => w.sev === 'cold') ? 'warn' : ''}`} style={{ marginBottom: 18 }} aria-busy={al.isLoading}>
          <b>Where we stand.</b> {verdict}
        </div>
      )}
      {warnings.length ? (
        <>
          <div className="lbl" style={{ margin: '0 0 8px' }}>
            Warnings
          </div>
          <div className="ualist">
            {warnings.map((w) => {
              const tip = d.products.find((p) => p.product_key === w.product_key)?.tip;
              return (
                <div key={w.key} className={`ual ${w.sev}`}>
                  <div>
                    <b>{w.product_name}</b>
                    <div>{w.why}</div>
                    <div className="sub" style={{ marginTop: 4 }}>
                      {tip || 'Find out why on the next call.'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : null}

      <div className="lbl" style={{ margin: '22px 0 10px' }}>
        Usage by product
      </div>
      {d.products.length ? (
        <div className="ustack">
          {d.products.map((p) => (
            <ProductPanel
              key={p.product_key}
              p={p}
              product={byKey.get(p.product_key)}
              asOf={d.as_of}
              clientName={c.name}
              onUpload={() => setUpload(true)}
              onTypeIn={() => setTypeIn(p.product_key)}
            />
          ))}
        </div>
      ) : (
        <div className="panel">
          <Empty title="No products yet">Add the client’s products in Settings → Products and usage to see their usage here.</Empty>
        </div>
      )}

      <div className="cols c2" style={{ marginTop: 26, gap: 22, alignItems: 'start' }}>
        {brief ? (
          <div className="panel">
            <div className="hd">
              <h3>Engagement this month</h3>
            </div>
            <div className="bd" style={{ display: 'grid', gap: 10 }}>
              {brief.engagement.length ? (
                brief.engagement.map((e) => (
                  <div key={e.activity_key} className="split" style={{ gap: 10 }} title={e.last_note ?? undefined}>
                    <span className={`chip ${STATE_CLS[e.state]}`} style={{ minWidth: 96, justifyContent: 'center' }}>
                      {e.label}
                    </span>
                    <span>{e.name}</span>
                    <span className="sub" style={{ marginLeft: 'auto' }}>
                      {e.sub_label}
                    </span>
                  </div>
                ))
              ) : (
                <div className="sub">No engagement activities are switched on.</div>
              )}
            </div>
          </div>
        ) : null}
        <div style={{ display: 'grid', gap: 22 }}>
          <div className="panel">
            <div className="hd">
              <h3>What is open</h3>
            </div>
            <div className="bd">
              {row ? (
                <dl className="kv">
                  <dt>Tasks</dt>
                  <dd>
                    {row.open_task_count}
                    {row.overdue_count ? (
                      <>
                        {' '}
                        <span className="chip cold">{row.overdue_count} overdue</span>
                      </>
                    ) : null}
                  </dd>
                  <dt>Escalations</dt>
                  <dd>{row.open_escalations || 'None'}</dd>
                  <dt>Client requests</dt>
                  <dd>{rq.data == null ? '…' : rq.data || 'None'}</dd>
                  <dt>Last heard</dt>
                  <dd>{row.last_contact_days == null ? 'Not recorded yet' : row.last_contact_days === 0 ? 'Today' : `${row.last_contact_days} days ago`}</dd>
                  <dt>Contact</dt>
                  <dd>{contact ? `${contact.name}${contact.title ? `, ${contact.title}` : ''}` : detail ? 'No contact yet' : '…'}</dd>
                </dl>
              ) : rows.error ? (
                <ErrorState error={rows.error} onRetry={() => void rows.refetch()} />
              ) : rows.isLoading ? (
                <Loading />
              ) : (
                <div className="sub">This client is not in your list.</div>
              )}
            </div>
          </div>
          <div className="panel">
            <div className="hd">
              <h3>Ideas to lift adoption</h3>
            </div>
            <div className="bd" style={{ display: 'grid', gap: 10 }}>
              {ideas.length ? (
                ideas.map((p) => (
                  <div key={p.product_key} className={`note ${tipCls(p, byKey.get(p.product_key)?.fall_pct ?? 15)}`} style={{ margin: 0 }}>
                    <b>{p.product_name}.</b> {p.tip}
                  </div>
                ))
              ) : (
                <div className="sub">Nothing needs changing. Keep the rhythm going.</div>
              )}
            </div>
          </div>
        </div>
      </div>
      {upload ? <UploadModal onClose={() => setUpload(false)} /> : null}
      {typeIn ? <TypeInModal clientId={c.id} productKey={typeIn} onClose={() => setTypeIn(null)} /> : null}
    </>
  );
}
